import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { sendChatMessage, markChatRead as markChatReadRest } from '../api/routes';
import type { ChatMessageResponse } from '../api/routes';
import { formatChatMessagingError } from '../utils/formatChatMessagingError';
import { readSendBlockedReason } from '../utils/readSendBlockedReason';
import { addNotification } from 'notifications';
import {
  chatKeys,
  invalidateChatLists,
  useChatContactShareQuery,
  useChatHistoryQuery,
} from '../api/queries';
import { useGlobalPresence } from '../store/GlobalPresenceContext';
import { useChatWebSocket } from '../store/ChatWebSocketContext';
import type { ChatMessage } from '../types';

export type SendMessageResult = 'ok' | 'failed' | 'closed';
import { isContactShareMessageText, toContactShareState } from '../utils/contactShareState';

interface UseChatChannelParams {
  currentUserId: string;
  selectedUserId: number | null;
  roomId: string | null;
  chatId: number | null;
  viewerRole?: 'tutor' | 'student';
}

const statusRank = (status: ChatMessage['status']): number => {
  switch (status) {
    case 'failed':
      return 0;
    case 'sending':
      return 1;
    case 'sent':
      return 2;
    case 'delivered':
      return 3;
    case 'read':
      return 4;
    default:
      return 2;
  }
};

const mergeStatus = (
  current: ChatMessage['status'],
  next: ChatMessage['status'],
): ChatMessage['status'] => (statusRank(next) > statusRank(current) ? next : current);

const applyReadReceipt = (
  messages: ChatMessage[],
  lastReadAt: string,
  readerId: number,
  currentUserIdNum: number,
): ChatMessage[] => {
  const readTime = new Date(lastReadAt).getTime();
  if (Number.isNaN(readTime)) {
    return messages;
  }
  return messages.map((msg) => {
    if (msg.sender_id !== currentUserIdNum || readerId === currentUserIdNum) {
      return msg;
    }
    const msgTime = new Date(msg.created_at || '').getTime();
    if (Number.isNaN(msgTime) || msgTime > readTime) {
      return msg;
    }
    return { ...msg, status: mergeStatus(msg.status, 'read') };
  });
};

const upsertHistoryMessage = (
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: number,
  message: ChatMessageResponse,
) => {
  queryClient.setQueryData<ChatMessageResponse[]>(chatKeys.messages(chatId), (old) => {
    const prev = old ?? [];
    const messageId = String(message.id);
    if (prev.some((item) => String(item.id) === messageId)) {
      return prev.map((item) =>
        String(item.id) === messageId
          ? {
              ...item,
              ...message,
              id: messageId,
              status: message.status ?? item.status,
            }
          : item,
      );
    }
    return [...prev, { ...message, id: messageId }].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  });
};

export const useChatChannel = ({
  currentUserId,
  selectedUserId,
  roomId,
  chatId,
  viewerRole,
}: UseChatChannelParams) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [otherUserOnline, setOtherUserOnline] = useState(false);
  const [sendBlockedReason, setSendBlockedReason] = useState<string | null>(null);

  const currentUserIdNum = parseInt(currentUserId, 10);
  const otherUserIdNum = selectedUserId || 0;

  const { data: apiMessages, isLoading: isApiLoading, isFetching } = useChatHistoryQuery(chatId);
  const {
    data: contactShareRaw,
    isLoading: isContactShareLoading,
    isFetched: isContactShareFetched,
    isError: isContactShareError,
  } = useChatContactShareQuery(chatId);
  const contactShare = toContactShareState(contactShareRaw);
  // Ready when query settled (success or error) or no chat selected.
  const isContactShareReady = !chatId || isContactShareFetched || isContactShareError;
  const queryClient = useQueryClient();
  const { subscribeRoom, sendChatMessage: sendWsMessage, markChatRead, isConnected } =
    useChatWebSocket();

  const { isUserOnline } = useGlobalPresence();

  const sentMessageIds = useRef<Set<string>>(new Set());
  const prevChatIdRef = useRef<number | null>(null);
  const otherLastReadAtRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (markReadTimeoutRef.current) {
        window.clearTimeout(markReadTimeoutRef.current);
      }
    };
  }, []);

  const markReadTimeoutRef = useRef<number | null>(null);

  const markReadForChat = useCallback(() => {
    if (!chatId || !roomId) {
      return;
    }
    if (markReadTimeoutRef.current) {
      window.clearTimeout(markReadTimeoutRef.current);
    }
    markReadTimeoutRef.current = window.setTimeout(() => {
      const sent = markChatRead({ chatId, roomId });
      if (!sent) {
        void markChatReadRest(chatId).catch(() => undefined);
      }
    }, 500); // debounce read receipts by 500ms
  }, [chatId, roomId, markChatRead]);

  useEffect(() => {
    if (!chatId) {
      if (prevChatIdRef.current != null) {
        void queryClient.cancelQueries({ queryKey: chatKeys.messages(prevChatIdRef.current) });
        void queryClient.cancelQueries({ queryKey: chatKeys.contactShare(prevChatIdRef.current) });
      }
      prevChatIdRef.current = null;
      setMessages([]);
      sentMessageIds.current.clear();
      otherLastReadAtRef.current = null;
      return;
    }

    if (prevChatIdRef.current !== chatId) {
      if (prevChatIdRef.current != null) {
        void queryClient.cancelQueries({ queryKey: chatKeys.messages(prevChatIdRef.current) });
        void queryClient.cancelQueries({ queryKey: chatKeys.contactShare(prevChatIdRef.current) });
      }
      prevChatIdRef.current = chatId;
      sentMessageIds.current.clear();
      otherLastReadAtRef.current = null;
      // Only clear while waiting for the first payload - keep cached history visible
      // when returning to a chat (avoids empty screen after WS-sent messages).
      const cached = queryClient.getQueryData<ChatMessageResponse[]>(chatKeys.messages(chatId));
      if (!cached?.length && (isApiLoading || isFetching) && apiMessages === undefined) {
        setMessages([]);
      }
    }

    if (apiMessages !== undefined && !isApiLoading) {
      const transformedMessages: ChatMessage[] = apiMessages.map((msg) => {
        const messageId = `api_${msg.id}`;
        const receiverId =
          msg.sender_id === currentUserIdNum ? otherUserIdNum : currentUserIdNum;

        sentMessageIds.current.add(messageId);

        return {
          id: messageId,
          text: msg.text,
          sender_id: msg.sender_id,
          receiver_id: receiverId,
          timestamp: new Date(msg.created_at).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
          }),
          created_at: msg.created_at,
          status: msg.status,
          sender_role: msg.sender_role,
          message_types: msg.message_types,
          isOwn: msg.sender_id === currentUserIdNum,
        };
      });

      let sorted: ChatMessage[] = transformedMessages.sort(
        (a, b) =>
          new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
      );

      if (otherLastReadAtRef.current) {
        sorted = applyReadReceipt(
          sorted,
          otherLastReadAtRef.current,
          otherUserIdNum,
          currentUserIdNum,
        );
      }

      setMessages((prev) => {
        // Keep optimistic/local messages that are not yet reflected in API history.
        const apiIds = new Set(sorted.map((m) => m.id));
        const pendingLocal = prev.filter((m) => {
          if (!m.isOwn) return false;
          if (!(m.status === 'sending' || m.id.startsWith('local_'))) return false;
          if (apiIds.has(m.id)) return false;
          const localCreated = new Date(m.created_at || 0).getTime();
          return !sorted.some((apiMsg) => {
            if (apiMsg.text !== m.text || apiMsg.sender_id !== m.sender_id) return false;
            const apiCreated = new Date(apiMsg.created_at || 0).getTime();
            return Math.abs(apiCreated - localCreated) < 15_000;
          });
        });
        if (!pendingLocal.length) {
          return sorted;
        }
        return [...sorted, ...pendingLocal].sort(
          (a, b) =>
            new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
        );
      });
    }
  }, [
    apiMessages,
    isApiLoading,
    isFetching,
    currentUserIdNum,
    otherUserIdNum,
    chatId,
    queryClient,
  ]);

  useEffect(() => {
    if (!selectedUserId) {
      setOtherUserOnline(false);
      return;
    }
    const online = isUserOnline(selectedUserId);
    setOtherUserOnline(online);
    // prod1: собеседник вошел в сеть - его клиент получает все отправленные ему сообщения,
    // поэтому свои «отправлено» становятся «доставлено» (как и на сервере в истории).
    if (online) {
      setMessages((prev) =>
        prev.some((m) => m.isOwn && m.status === 'sent')
          ? prev.map((m) => (m.isOwn && m.status === 'sent' ? { ...m, status: 'delivered' } : m))
          : prev,
      );
    }
  }, [selectedUserId, isUserOnline]);

  useEffect(() => {
    setSendBlockedReason(null);
  }, [chatId, roomId]);

  useEffect(() => {
    if (!roomId || !chatId || !selectedUserId) {
      return;
    }

    const handleWsEvent = (event: { type: string; data: Record<string, unknown> }) => {
      if (event.type === 'contact_share') {
        void queryClient.invalidateQueries({ queryKey: chatKeys.contactShare(chatId) });
        void queryClient.invalidateQueries({ queryKey: chatKeys.messages(chatId) });
        void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
        return;
      }

      if (event.type === 'read') {
        const readerId = parseInt(String(event.data.readerId ?? 0), 10);
        const lastReadAt =
          typeof event.data.last_read_at === 'string' ? event.data.last_read_at : null;
        if (readerId && readerId === currentUserIdNum) {
          // prod1: свое прочтение (в т.ч. с другой вкладки) - обновить счетчики непрочитанного в списках.
          void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
          void queryClient.invalidateQueries({ queryKey: ['student_bulletin_tutors'] });
          void queryClient.invalidateQueries({ queryKey: ['student_chat_bulletin_threads'] });
          return;
        }
        if (!readerId || !lastReadAt) {
          return;
        }
        otherLastReadAtRef.current = lastReadAt;
        setMessages((prev) =>
          applyReadReceipt(prev, lastReadAt, readerId, currentUserIdNum),
        );
        return;
      }

      if (event.type === 'delivered') {
        const apiMessageId = event.data.apiMessageId;
        const localId = event.data.localId as string | undefined;
        setMessages((prev) =>
          prev.map((msg) => {
            const matches =
              (apiMessageId && msg.id === `api_${apiMessageId}`) ||
              (localId && msg.id === localId);
            if (!matches || !msg.isOwn) {
              return msg;
            }
            return { ...msg, status: mergeStatus(msg.status, 'delivered') };
          }),
        );
        return;
      }

      if (event.type !== 'message') {
        if (event.type === 'error') {
          const blocked = readSendBlockedReason(event.data);
          if (blocked) {
            setSendBlockedReason(blocked);
          }
          if (event.data?.local_id) {
            const localId = String(event.data.local_id);
            setMessages((prev) =>
              prev.map((msg) => (msg.id === localId ? { ...msg, status: 'failed' } : msg)),
            );
          }
        }
        return;
      }

      const data = event.data;
      const messageRoomId = data.roomId as string | undefined;
      if (messageRoomId && messageRoomId !== roomId) {
        return;
      }

      const apiMessageId = data.apiMessageId;
      const messageId = apiMessageId
        ? `api_${apiMessageId}`
        : (data.localId as string) || `ws_${data.timestamp}_${data.sender}`;
      const senderId = data.sender ? parseInt(String(data.sender), 10) : 0;

      if (!senderId || (senderId !== currentUserIdNum && senderId !== selectedUserId)) {
        return;
      }

      const createdAt =
        typeof data.timestamp === 'string'
          ? data.timestamp
          : new Date().toISOString();
      const text = String(data.text ?? '');
      // prod1: типы сообщения (например, отклик репетитора с ценой) приходят и в realtime-событии.
      const messageTypes = Array.isArray(data.types)
        ? (data.types as unknown[]).filter((t): t is string => typeof t === 'string')
        : undefined;

      // Persist confirmed server messages into history cache so leave/return
      // does not fall back to a stale empty React Query payload.
      if (apiMessageId) {
        upsertHistoryMessage(queryClient, chatId, {
          id: String(apiMessageId),
          text,
          sender_id: senderId,
          receiver_id: senderId === currentUserIdNum ? selectedUserId : currentUserIdNum,
          created_at: createdAt,
          updated_at: createdAt,
          status: 'sent',
          message_types: messageTypes,
          sender_role:
            senderId === currentUserIdNum
              ? viewerRole
              : viewerRole === 'tutor'
                ? 'student'
                : viewerRole === 'student'
                  ? 'tutor'
                  : undefined,
        });
      }

      if (sentMessageIds.current.has(messageId)) {
        if (senderId === currentUserIdNum && data.localId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === data.localId
                ? { ...msg, id: messageId, status: mergeStatus(msg.status, 'sent') }
                : msg,
            ),
          );
        }
        return;
      }

      sentMessageIds.current.add(messageId);
      if (data.localId) {
        sentMessageIds.current.add(String(data.localId));
      }

      if (senderId === currentUserIdNum && data.localId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === data.localId
              ? { ...msg, id: messageId, status: mergeStatus(msg.status, 'sent') }
              : msg,
          ),
        );
        return;
      }

      const inferSenderRole = (id: number): 'tutor' | 'student' | undefined => {
        if (!viewerRole) return undefined;
        const isOwn = id === currentUserIdNum;
        if (isOwn) return viewerRole;
        return viewerRole === 'tutor' ? 'student' : 'tutor';
      };

      const newMessage: ChatMessage = {
        id: messageId,
        text,
        sender_id: senderId,
        receiver_id: senderId === currentUserIdNum ? selectedUserId : currentUserIdNum,
        timestamp: new Date(createdAt).toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        created_at: createdAt,
        status: 'sent',
        sender_role: inferSenderRole(senderId),
        message_types: messageTypes,
        isOwn: senderId === currentUserIdNum,
      };

      setMessages((prev) => {
        const exists = prev.some((m) => {
          if (m.id === newMessage.id) return true;
          const timeDiff = Math.abs(
            new Date(m.created_at || 0).getTime() - new Date(newMessage.created_at || 0).getTime(),
          );
          return (
            timeDiff < 2000 &&
            m.text === newMessage.text &&
            m.sender_id === newMessage.sender_id
          );
        });
        if (exists) return prev;

        if (senderId !== currentUserIdNum) {
          invalidateChatLists(queryClient);
          markReadForChat();
          if (isContactShareMessageText(text)) {
            void queryClient.invalidateQueries({ queryKey: chatKeys.contactShare(chatId) });
          }
        }

        return [...prev, newMessage].sort(
          (a, b) =>
            new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
        );
      });
    };

    return subscribeRoom(roomId, chatId, handleWsEvent);
  }, [
    roomId,
    chatId,
    selectedUserId,
    currentUserIdNum,
    viewerRole,
    queryClient,
    subscribeRoom,
    markReadForChat,
  ]);

  useEffect(() => {
    if (!chatId || !roomId || !isConnected) {
      return;
    }
    markReadForChat();
  }, [chatId, roomId, isConnected, messages.length, markReadForChat]);

  const sendMessage = useCallback(
    async (text: string): Promise<SendMessageResult> => {
      if (!chatId || !selectedUserId || !roomId) {
        return 'failed';
      }

      const messageId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = new Date().toISOString();

      const pendingMessage: ChatMessage = {
        id: messageId,
        text,
        sender_id: currentUserIdNum,
        receiver_id: selectedUserId,
        timestamp: new Date().toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        created_at: timestamp,
        status: 'sending',
        sender_role: viewerRole,
        isOwn: true,
      };

      setMessages((prev) =>
        [...prev, pendingMessage].sort(
          (a, b) =>
            new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
        ),
      );

      sentMessageIds.current.add(messageId);

      const wsSent = sendWsMessage({
        chatId,
        roomId,
        text,
        localId: messageId,
      });

      if (wsSent) {
        invalidateChatLists(queryClient);
        // Mark history stale so remount/refocus refetches if WS ack was missed.
        void queryClient.invalidateQueries({ queryKey: chatKeys.messages(chatId) });

        // Timeout to mark as failed if server doesn't echo within 10s
        setTimeout(() => {
          setMessages((prev) => {
            const msg = prev.find((m) => m.id === messageId);
            if (msg && msg.status === 'sending') {
              return prev.map((m) => (m.id === messageId ? { ...m, status: 'failed' } : m));
            }
            return prev;
          });
        }, 10000);

        return 'ok';
      }

      try {
        const saved = await sendChatMessage(chatId, text, roomId);
        const apiMessageId = `api_${saved.id}`;
        const createdAt = saved.date_send || timestamp;
        sentMessageIds.current.add(apiMessageId);

        upsertHistoryMessage(queryClient, chatId, {
          id: String(saved.id),
          text: saved.msg,
          sender_id: saved.user_id,
          receiver_id: selectedUserId,
          created_at: createdAt,
          updated_at: saved.date_change || createdAt,
          status: 'sent',
          sender_role: viewerRole,
        });

        setMessages((prev) =>
          prev
            .map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    id: apiMessageId,
                    status: 'sent' as const,
                    created_at: createdAt,
                    timestamp: new Date(createdAt).toLocaleTimeString('ru-RU', {
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  }
                : msg,
            )
            .sort(
              (a, b) =>
                new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
            ),
        );

        invalidateChatLists(queryClient);
        return 'ok';
      } catch (error: unknown) {
        console.error('[useChatChannel] Ошибка отправки:', error);
        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, status: 'failed' } : msg)),
        );
        const friendly = formatChatMessagingError(error);
        if (friendly) {
          addNotification(friendly, 'info');
          return 'closed';
        }
        return 'failed';
      }
    },
    [
      chatId,
      selectedUserId,
      roomId,
      currentUserIdNum,
      viewerRole,
      queryClient,
      sendWsMessage,
    ],
  );

  const isLoadingHistory = Boolean(
    chatId && (isApiLoading || (isFetching && apiMessages === undefined)),
  );

  return {
    messages,
    sendMessage,
    otherUserOnline,
    sendBlockedReason,
    contactShare,
    isContactShareReady,
    isContactShareLoading,
    isLoadingHistory,
    isConnected,
    markReadForChat,
  };
};
